package com.kinoshazam.screens.allVideo.medium

import androidx.compose.foundation.Image
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.itemsIndexed
import androidx.compose.foundation.lazy.grid.rememberLazyGridState
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.kinoshazam.uiKit.components.button.OutlinedCircleIconButton
import com.kinoshazam.uiKit.components.textLabel.TextLabel
import com.kinoshazam.uiKit.components.textLabel.TextLabelState
import com.kinoshazam.uiKit.components.videoItem.MediumVideoItem
import com.kinoshazam.uiKit.components.videoItem.mediumBannerSkeletons
import com.kinoshazam.uiKit.components.videoItem.VideoState
import com.kinoshazam.uiKit.theme.BoldLarge
import com.kinoshazam.uiKit.theme.UiKitTheme
import com.kinoshazam.view.ErrorView
import kinoshazam.resources.generated.resources.Res
import kinoshazam.resources.generated.resources.back
import kinoshazam.resources.generated.resources.error
import kinoshazam.resources.generated.resources.movie
import kinoshazam.resources.generated.resources.repeat
import kinoshazam.resources.generated.resources.search
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import presentation.screens.allVideos.AllVideosMVIAction
import presentation.screens.allVideos.AllVideosState

private val DesktopContentMaxWidth = 1200.dp
private val ContentHorizontalPadding = 60.dp
private val ContentTopPadding = 40.dp
private val ContentBottomPadding = 90.dp
private val GridSpacing = 32.dp
private val MinCardWidth = 160.dp

@Composable
fun MediumAllVideosContent(
    state: AllVideosState,
    onAction: (AllVideosMVIAction) -> Unit,
) {
    Scaffold(
        containerColor = UiKitTheme.colors.color_3,
        content = { paddingValues ->
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(paddingValues)
            ) {
                when {
                    state.isError -> ErrorScreen(onAction)
                    else -> ContentScreen(state, onAction)
                }
            }
        }
    )
}

@Composable
private fun ErrorScreen(onAction: (AllVideosMVIAction) -> Unit) {
    ErrorView(
        textError = stringResource(Res.string.error),
        textAction = stringResource(Res.string.repeat),
        onAction = { onAction(AllVideosMVIAction.OnReload) }
    )
}

@Composable
private fun LoadingScreen() {
    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center,
    ) {
        CircularProgressIndicator(
            modifier = Modifier.size(24.dp),
            color = UiKitTheme.colors.color_1,
            strokeWidth = 2.dp,
        )
    }
}

@Composable
private fun ContentScreen(
    state: AllVideosState,
    onAction: (AllVideosMVIAction) -> Unit,
) {
    val scrollState = rememberLazyGridState()
    val fetchNextPage: Boolean by remember {
        derivedStateOf {
            val currentVideoCount = scrollState.layoutInfo.totalItemsCount - 1
            val lastVisibleIndex =
                scrollState.layoutInfo.visibleItemsInfo.lastOrNull()?.index
                    ?: return@derivedStateOf false
            return@derivedStateOf lastVisibleIndex >= currentVideoCount - 5
        }
    }

    LaunchedEffect(key1 = fetchNextPage) {
        if (fetchNextPage) {
            onAction(AllVideosMVIAction.FetchNextPage)
        }
    }

    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val contentWidth = maxWidth.coerceAtMost(DesktopContentMaxWidth)
        val availableWidth = contentWidth - ContentHorizontalPadding * 2
        val columnCount = maxOf(
            4,
            ((availableWidth + GridSpacing) / (MinCardWidth + GridSpacing)).toInt()
        )

        Column(
            modifier = Modifier
                .align(Alignment.TopCenter)
                .widthIn(max = DesktopContentMaxWidth)
                .fillMaxWidth()
                .fillMaxSize()
                .padding(
                    start = ContentHorizontalPadding,
                    end = ContentHorizontalPadding,
                    top = ContentTopPadding,
                    bottom = ContentBottomPadding,
                ),
        ) {
            TopBar(
                title = state.title,
                onBack = { onAction(AllVideosMVIAction.BackPressed) },
                onSearch = { onAction(AllVideosMVIAction.SearchClicked) },
            )
            Spacer(modifier = Modifier.size(32.dp))
            LazyVerticalGrid(
                state = scrollState,
                modifier = Modifier
                    .weight(1f)
                    .fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(GridSpacing),
                horizontalArrangement = Arrangement.spacedBy(GridSpacing),
                columns = GridCells.Fixed(columnCount),
            ) {
                val videos = state.videos
                if (videos == null) {
                    mediumBannerSkeletons()
                } else itemsIndexed(videos) { _, video ->
                    MediumVideoItem(
                        modifier = Modifier.fillMaxWidth(),
                        state = VideoState(
                            id = video.id ?: -1,
                            title = video.title.orEmpty(),
                            image = video.imageUrl,
                            placeHolder = painterResource(Res.drawable.movie),
                            placeHolderColor = UiKitTheme.colors.color_5,
                            onAction = { id ->
                                onAction(AllVideosMVIAction.OpenDetails(id, video.isTvShow))
                            }
                        ),
                    )
                }
                if (state.isNextLoading) {
                    item {
                        Text(
                            text = "Load next page",
                            color = UiKitTheme.colors.color_2,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun TopBar(
    title: String,
    onBack: () -> Unit,
    onSearch: () -> Unit,
) {
    Box(modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier.align(Alignment.CenterStart),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Image(
                painter = painterResource(Res.drawable.back),
                colorFilter = ColorFilter.tint(UiKitTheme.colors.color_1),
                contentDescription = null,
                modifier = Modifier.clickable(onClick = onBack)
            )
            Spacer(modifier = Modifier.size(16.dp))
            OutlinedCircleIconButton(
                painter = painterResource(Res.drawable.search),
                onClick = onSearch,
                size = 44.dp,
                contentDescription = "Search",
            )
        }
        TextLabel(
            state = TextLabelState(
                text = title,
                color = UiKitTheme.colors.color_2,
                textStyle = BoldLarge,
                textAlign = TextAlign.Center,
                maxLines = 1,
            ),
            modifier = Modifier
                .align(Alignment.Center)
                .padding(horizontal = 120.dp),
        )
    }
}
